// Dependencies
import React from "react";

// Modules
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

// App
import { serverUrl } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import PostTitle from "../../partials/postTitle";
import { alertMessages } from "../../partials/alertMessages";
import Error from "../../partials/error";
import SkeletonSingleScreen from "../../partials/skeleton-screens/skeletonSingleScreen";
import UserImage from "../../partials/userImage";
import PostBody from "../../partials/postBody";
import { apiEndpoints } from "../../config";

// UI components
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Link, Typography } from "@mui/material";
import TimeIcon from "@mui/icons-material/AccessTimeOutlined";
import LocationIcon from "@mui/icons-material/LocationOnOutlined";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import CallIcon from "@mui/icons-material/CallOutlined";
import SinglesHeader from "../../partials/singlesHeader";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import ClientTheme from "../../core/clientTheme";

export default function CompanyDirectorySingle(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const clientTheme = { ...ClientTheme() };

  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const user = useSelector((state) => state).authReducer.user;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");

  useEffect(() => {
    loadNode();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (post) {
      getCategory();
    }
    // eslint-disable-next-line
  }, [post]);


  /**
   * @function setData
   * @description Updates the state of the component upon data retrival
   * @param {object} _data
   * @param {object} _realm
   */

  const setData = (_data) => {
    setPost(_data);
    setIsLoading(false);
    setIsError(false);
    // getComments();
    // getReactions();
    getCategory(_data.field_category);
  };

  /**
   * @function setError
   * @description Updates the state of the component upon an error
   * @param {boolean} _isError
   * @param {int} _errorStatus
   * @param {string} _errorMessage
   */
  const setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);
    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  const loadNode = () => {
    const nid = params.id;

    if (nid) {
      getContent(nid);
      // getComments(nid)
    } else {
      setError(true, 0, alertMessages.contentError);
    }
  };

  /**
   * @function getContent
   * @description Retrieves the data from an API / Fallback to local realm object if there is no connection
   */
  const getContent = (nid) => {
    let path = `${apiEndpoints.company_directory}?nid=${nid}`;

    if (hash) {
      path = `/api/v2/shared?bundle=company_directory&nid=${nid}&authkey=${hash}`;
    }

    getOnlineNode(path, user.access_token)
      .then((response) => {
        if (response.data.rows.length > 0) {
          setData(response.data.rows[0]);
          // logCustomEvent('read', response.data.rows[0], 'Event');
        } else {
          setError(true, 200, alertMessages.noData);
        }
      })
      .catch((_error) => { });
  };

  const getCategory = (id) => {
    if (post.field_category) {
      getOnlineNode(
        `api/categories?tid=${post.field_category}`,
        user.access_token
      )
        .then((response) => {
          let categoryTemp =
            response.data.length > 0 ? response.data[0] : { title: "" };
          setCategoryName(categoryTemp.title);
        })
        .catch((_error) => {
          if (_error.response) {
            setError(true, _error.response.status, _error.response.statusText);
          } else if (_error.request) {
            //console.log('@rest request: ', _error.request);
            setError(true, 0, alertMessages.requestError.message);
          } else {
            //console.log('@rest unknown: ', _error);
            setError(true, 0, alertMessages.unkownError.message);
          }
        });
    }
  };

  const cleanString = (inputString) => {
    const cleanedString = inputString.replace(/&\s/g, "");

    return cleanedString;
  };

  const infoProperty = (propertyString, icon) => {
    return (
      <Col xs={5} sm={3} >
        <Row className="ps-4 ms-2">
          <Col xs={"auto"} className="pe-3">
            {icon}
          </Col>
          <Col className="px-0">
            <Typography
              owner="client"
              variant="body2"
              color="clientText.main"
              lh="x-wide"
              weight="bold"
              singles="true"
            >
              {propertyString}:
            </Typography>
          </Col>
        </Row>
      </Col>
    );
  };

  const infoValue = (valueString, link) => {
    return (
      <Col xs={7} sm={9} className="flex-grow-1 ps-4">
        <Row>
          <Col>
            <Typography
              owner="client"
              variant="body2"
              className="d-flex justify-content-between"
              color="clientText.main"
              lh="x-wide"
              weight="light"
              singles="true"
            >
              {link ?
                <Link
                  owner="client"
                  variant="body2"
                  className="d-flex justify-content-between w-100"
                  singles="true"
                  weight="light"
                  lh="x-wide"
                  color="clientText.main"
                  hoverbehavior={clientTheme.components.link.hoverbehavior}
                  href={link}
                  target='_blank'
                  rel="noreferrer">
                  {valueString}
                  <OpenInNewIcon fontSize="small" color="clientPrimary" />
                </Link>
                :
                valueString
              }
            </Typography>
          </Col>
        </Row>
      </Col>
    );
  };

  const contactGroup = (groupItem, index, icon) => {
    return (
      <Row
        id={`group-item-${index}`}
        className="group-item-container pt-2 pb-3 mt-3"
      >
        <Col xs={"auto"} className="ps-1 pe-3">
          {icon}
        </Col>
        <Col>
          <Row className="contact-group-name mb-2">
            <Col xs={12}>
              <Typography
                owner="client"
                className="pt-2 mb-3"
                variant="h5"
                color="clientText.main"
                singles="true"
              >
                {groupItem.field_section_title}
              </Typography>
            </Col>
          </Row>
          {groupItem.field_phone_number &&
            groupItem.field_phone_number !== "" && (
              <Row className="contact-group-phone ps-4 mb-2">
                <Col xs={12}>
                  <Row className="gx-0 display-flex align-items-center">
                    <Col xs="auto" className="pe-3">
                      <CallIcon fontSize="small" color="clientPrimary" />
                    </Col>
                    <Col>
                      <Typography
                        className="group-item-phone"
                        variant="body2"
                        color="clientText.main"
                      >
                        <Link
                          owner="client"
                          variant="body2"
                          className="d-flex justify-content-between"
                          singles="true"
                          weight="light"
                          lh="x-wide"
                          color="clientText.main"
                          hoverbehavior={clientTheme.components.link.hoverbehavior}
                          href={`tel:${groupItem.field_phone_number}`}
                          target='_blank'
                          rel="noreferrer">
                          {groupItem.field_phone_number}
                          <OpenInNewIcon fontSize="small" color="clientPrimary" />
                        </Link>
                      </Typography>
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}
          {groupItem.field_email && groupItem.field_email !== "" && (
            <Row className="contact-group-mail ps-4 mb-2">
              <Col xs={12}>
                <Row className="gx-0 display-flex align-items-center">
                  <Col xs="auto" className="pe-3">
                    <EmailIcon fontSize="small" color="clientPrimary" />
                  </Col>
                  <Col>
                    <Typography
                      className="group-item-email"
                      variant="body2"
                      color="clientText.main"
                    >
                      <Link
                        owner="client"
                        variant="body2"
                        className="d-flex justify-content-between"
                        singles="true"
                        weight="light"
                        lh="x-wide"
                        color="clientText.main"
                        hoverbehavior={clientTheme.components.link.hoverbehavior}
                        href={`mailto:${groupItem.field_email}`}
                        target='_blank'
                        rel="noreferrer">
                        {groupItem.field_email}
                        <OpenInNewIcon fontSize="small" color="clientPrimary" />
                      </Link>
                    </Typography>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    );
  };

  if (isLoading) {
    return <SkeletonSingleScreen />;
  } else {
    if (isError) {
      return <Error status={errorStatus} message={errorMessage} />;
    } else {

      let category = "Uncategorised";

      if (categoryName) {
        category = categoryName;
      }

      if (post.category_labels) {
        category = post.category_labels;
      }

      return (
        <main className="company-directory  single">
          <article className="article">
            <SinglesHeader
              route={"/company_directory"}
              backBtnText={"Companyapp Directory"}
              category={category}
              timestamp={post.changed}
              hash={hash}
            />
            <div className="company-directory-page-body">
              <Row className="information-heading-container justify-content-center mb-4">
                <Col xs={5} sm={3} className="avatar-container mb-4 mb-sm-0">
                  <UserImage
                    className="ratio ratio-1x1"
                    owner="client"
                    sub_variant="user-image"
                    name={cleanString(post.title)}
                    image={post.field_featured_image}
                  />
                </Col>
                <Col xs={7} sm={9} className="contact-info-container ps-4">
                  <Row className="mb-3">
                    <Col>
                      <PostTitle
                        title={post.title}
                        headingLevel={"h1"}
                        titleColor="clientText.dark"
                      />
                    </Col>
                  </Row>
                  <Row className="company-directory-body mb-3">
                    <Col xs={12}>
                      <div className="article__body">
                        <PostBody body={post.body} />
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="company-directory-information-body-container mb-4">
                <Col className="" xs={12}>
                  <Row className="company-directory-hours d-flex align-items-center mb-3">
                    {infoProperty("Office hours", (<TimeIcon fontSize="small" color="clientPrimary" />))}
                    {infoValue(`${post.field_opening_times}`, false)}
                  </Row>
                  <Row className="company-directory-location d-flex align-content-center align-items-center mb-3">
                    {infoProperty("Location", (<LocationIcon fontSize="small" color="clientPrimary" />))}
                    {infoValue(`${post.field_address}`, `http://maps.google.com/?q=${post.field_address}`)}
                  </Row>
                </Col>
              </Row>
              {post.field_contact_group.length > 0 && (
                <>
                  {post.field_contact_group.map((e, i) => {
                    return contactGroup(e, i);
                  })}
                </>
              )}
            </div>
          </article>
        </main>
      );
    }
  }
}
